import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificacionesService } from '../services/api';
import './NotificationBell.css';

const formatFechaNotif = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short', hour12: false });
};

const NotificationBell = ({ rol }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const loadUnread = useCallback(async () => {
    try {
      const data = await notificacionesService.getUnreadCount(rol);
      setUnreadCount(typeof data?.unread_count === 'number' ? data.unread_count : 0);
    } catch (_) {
      /* silencioso */
    }
  }, [rol]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notificacionesService.list({ rol, page: 1, page_size: 20 });
      setItems(Array.isArray(data?.results) ? data.results : []);
      setUnreadCount(typeof data?.unread_count === 'number' ? data.unread_count : 0);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [rol]);

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    const onFocus = () => loadUnread();
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadUnread();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadUnread]);

  useEffect(() => {
    if (open) loadList();
  }, [open, loadList, rol]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => setOpen((v) => !v);

  const onClickItem = async (n) => {
    if (!n.leida) {
      try {
        await notificacionesService.marcarLeida(n.id);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, leida: true } : x)));
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch (_) {
        /* igual navega */
      }
    }
    setOpen(false);
    if (n.link) {
      navigate(n.link);
    }
  };

  const marcarTodas = async () => {
    try {
      await notificacionesService.marcarTodas(rol);
      setItems((prev) => prev.map((x) => ({ ...x, leida: true })));
      setUnreadCount(0);
    } catch (_) {
      /* silencioso */
    }
  };

  return (
    <div className="notif-bell-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`dashboard-bell-btn${open ? ' is-open' : ''}`}
        aria-label="Notificaciones"
        title="Notificaciones"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={toggle}
      >
        <svg
          className="dashboard-bell-icon"
          viewBox="0 0 24 24"
          width="28"
          height="28"
          aria-hidden="true"
          focusable="false"
        >
          <path
            fill="currentColor"
            d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6V11a7 7 0 1 0-14 0v5l-2 2v1h18v-1l-2-2Zm-2 1H7v-6a5 5 0 1 1 10 0v6Z"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="notif-bell-badge" aria-label={`${unreadCount} sin leer`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-bell-panel" role="dialog" aria-label="Panel de notificaciones">
          <div className="notif-bell-panel-header">
            <strong>Notificaciones</strong>
            {unreadCount > 0 && (
              <button type="button" className="notif-bell-mark-all" onClick={marcarTodas}>
                Marcar todas leídas
              </button>
            )}
          </div>
          <div className="notif-bell-panel-body">
            {loading && items.length === 0 ? (
              <p className="notif-bell-empty">Cargando…</p>
            ) : items.length === 0 ? (
              <p className="notif-bell-empty">No tenés notificaciones.</p>
            ) : (
              <ul className="notif-bell-list">
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      className={`notif-bell-item${n.leida ? '' : ' unread'}`}
                      onClick={() => onClickItem(n)}
                    >
                      <span className="notif-bell-item-title">{n.titulo}</span>
                      {n.mensaje && <span className="notif-bell-item-msg">{n.mensaje}</span>}
                      <span className="notif-bell-item-date">{formatFechaNotif(n.created_at)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
